// biome-ignore-all lint/a11y/noLabelWithoutControl: staff-only admin surface; these labels are visual captions for the adjacent control, intentionally not wired via htmlFor (Task #25).
// =============================================================================
// StandardCostsAdmin — edit the device + stationery cost catalogue
// =============================================================================
// Renders: a form listing each device type and stationery kit with its cost.
//          Saved into `portal_config`. Invalidates the in-memory cache
//          (useStandardCosts hook) on save so the wizard picks up new values
//          immediately.
// Props:
//   inline - if true, render without the full-page chrome (for embedding)
//   user   - admin user (for audit logging on save)
//
// Pulled out of AdminPages.jsx as part of the Admin file split (May 2026).
// =============================================================================
import React from "react";
import { getConfigValue, upsertConfig } from "../../data/portalConfig";
import { invalidateStandardCostsCache } from "../../hooks/useStandardCosts";
import { logAudit } from "../../utils/auditLog";
import { DEFAULT_STANDARD_COSTS } from "../../utils/formConstants";

export function StandardCostsAdmin({ inline, user }) {
  var state = React.useState(null);
  var form = state[0];
  var setForm = state[1];
  var saving = React.useState(false);
  var isSaving = saving[0];
  var setIsSaving = saving[1];
  var msg = React.useState("");
  var savedMsg = msg[0];
  var setSavedMsg = msg[1];
  var open = React.useState(false);
  var isOpen = open[0];
  var setIsOpen = open[1];
  // Edit/Save gating (John, 24 Aug 2026): read-only until Edit, then Save/Cancel.
  const [editing, setEditing] = React.useState(false);
  const snapRef = React.useRef(null);

  React.useEffect(
    function () {
      if ((!isOpen && !inline) || form !== null) return;
      getConfigValue("standard_costs").then(function (value) {
        var existing = value ? value : {};
        setForm(Object.assign({}, DEFAULT_STANDARD_COSTS, existing));
      });
    },
    [isOpen]
  );

  function setF(k, v) {
    setForm(function (prev) {
      return Object.assign({}, prev, { [k]: v });
    });
  }

  async function save() {
    setIsSaving(true);
    // Convert string inputs to numbers
    var toSave = {};
    Object.keys(form).forEach(function (k) {
      toSave[k] = parseFloat(form[k]) || 0;
    });
    // Upsert into portal_config
    var result = await upsertConfig("standard_costs", toSave);
    setIsSaving(false);
    if (result.error) {
      setSavedMsg(`Save failed: ${result.error.message}`);
    } else {
      invalidateStandardCostsCache();
      logAudit("standard_costs_updated", { costs: toSave, by: user?.email || "" });
      setSavedMsg("Saved ✓");
      setEditing(false);
    }
    setTimeout(function () {
      setSavedMsg("");
    }, 3000);
  }

  return (
    <div
      className={inline ? "" : "border border-zephyr-border rounded-[10px] mt-8 overflow-hidden"}
    >
      {!inline && (
        <button
          type="button"
          onClick={function () {
            setIsOpen(function (v) {
              return !v;
            });
          }}
          className="w-full bg-zephyr-teal-dark text-white py-[13px] px-5 flex justify-between items-center cursor-pointer border-none text-left"
          style={{ font: "inherit" }}
        >
          <span className="font-bold text-[15px]">
            Standard Costs (Stationery, Devices &amp; Shoes)
          </span>
          <span className="text-xs opacity-80">{isOpen ? "^ collapse" : "v expand"}</span>
        </button>
      )}
      {(inline || isOpen) && (
        <div className={inline ? "" : "p-5"}>
          <p className="text-[13px] text-zephyr-muted mt-0 mb-5">
            These costs are used to pre-fill estimated amounts and enforce maximums for stationery
            (QLD), devices, and shoes. They are shown to the DFV Service as estimates — not final
            invoice amounts. Update whenever standard pricing changes.
          </p>
          {form === null ? (
            <div className="text-zephyr-muted text-[13px]">Loading...</div>
          ) : (
            <div>
              <div className="font-bold text-[13px] text-zephyr-teal-dark mb-3 uppercase tracking-[0.4px]">
                Stationery Packs
              </div>
              <div className="grid grid-cols-1 gap-4 mb-5">
                <div>
                  <label className="text-[13px] font-semibold text-zephyr-text block mb-[5px]">
                    Primary school pack ($){" "}
                    <span className="font-normal text-zephyr-muted">Prep–Year 6</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    disabled={!editing}
                    value={form.stationery_primary}
                    onChange={function (e) {
                      setF("stationery_primary", e.target.value);
                    }}
                    className="py-2 px-[10px] rounded-[7px] border border-zephyr-border text-[14px] box-border w-[140px] text-right disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  />
                </div>
                <div>
                  <label className="text-[13px] font-semibold text-zephyr-text block mb-[5px]">
                    Secondary school pack ($){" "}
                    <span className="font-normal text-zephyr-muted">Year 7–12</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    disabled={!editing}
                    value={form.stationery_secondary}
                    onChange={function (e) {
                      setF("stationery_secondary", e.target.value);
                    }}
                    className="py-2 px-[10px] rounded-[7px] border border-zephyr-border text-[14px] box-border w-[140px] text-right disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  />
                </div>
              </div>
              <div className="font-bold text-[13px] text-zephyr-teal-dark mb-3 uppercase tracking-[0.4px]">
                Devices
              </div>
              <div className="grid grid-cols-1 gap-4 mb-5">
                <div>
                  <label className="text-[13px] font-semibold text-zephyr-text block mb-[5px]">
                    Laptop ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    disabled={!editing}
                    value={form.laptop}
                    onChange={function (e) {
                      setF("laptop", e.target.value);
                    }}
                    className="py-2 px-[10px] rounded-[7px] border border-zephyr-border text-[14px] box-border w-[140px] text-right disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  />
                </div>
                <div>
                  <label className="text-[13px] font-semibold text-zephyr-text block mb-[5px]">
                    iPad ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    disabled={!editing}
                    value={form.ipad}
                    onChange={function (e) {
                      setF("ipad", e.target.value);
                    }}
                    className="py-2 px-[10px] rounded-[7px] border border-zephyr-border text-[14px] box-border w-[140px] text-right disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  />
                </div>
              </div>
              <div className="font-bold text-[13px] text-zephyr-teal-dark mb-3 uppercase tracking-[0.4px]">
                Shoes — Maximum Costs
              </div>
              <div className="text-xs text-zephyr-muted mb-3">
                These are the maximum amounts Zephyr will fund. The portal enforces these limits and
                shows them to DFV Service users.
              </div>
              <div className="grid grid-cols-1 gap-4 mb-5">
                <div>
                  <label className="text-[13px] font-semibold text-zephyr-text block mb-[5px]">
                    Joggers — Primary max ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    disabled={!editing}
                    value={form.joggers_primary}
                    onChange={function (e) {
                      setF("joggers_primary", e.target.value);
                    }}
                    className="py-2 px-[10px] rounded-[7px] border border-zephyr-border text-[14px] box-border w-[140px] text-right disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  />
                </div>
                <div>
                  <label className="text-[13px] font-semibold text-zephyr-text block mb-[5px]">
                    Joggers — Secondary max ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    disabled={!editing}
                    value={form.joggers_secondary}
                    onChange={function (e) {
                      setF("joggers_secondary", e.target.value);
                    }}
                    className="py-2 px-[10px] rounded-[7px] border border-zephyr-border text-[14px] box-border w-[140px] text-right disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  />
                </div>
                <div>
                  <label className="text-[13px] font-semibold text-zephyr-text block mb-[5px]">
                    Leather Shoes max ($){" "}
                    <span className="font-normal text-zephyr-muted">secondary only</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    disabled={!editing}
                    value={form.leather_shoes}
                    onChange={function (e) {
                      setF("leather_shoes", e.target.value);
                    }}
                    className="py-2 px-[10px] rounded-[7px] border border-zephyr-border text-[14px] box-border w-[140px] text-right disabled:bg-zephyr-panel-bg disabled:text-zephyr-muted"
                    style={{ fontFamily: "inherit" }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                {!editing ? (
                  <button
                    type="button"
                    onClick={function () {
                      snapRef.current = form;
                      setSavedMsg("");
                      setEditing(true);
                    }}
                    className="bg-zephyr-teal text-white border-none rounded-[7px] py-[9px] px-6 text-[14px] font-bold cursor-pointer"
                    style={{ fontFamily: "inherit" }}
                  >
                    Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={save}
                      disabled={isSaving}
                      className={[
                        "bg-zephyr-teal text-white border-none rounded-[7px] py-[9px] px-6 text-[14px] font-bold",
                        isSaving ? "cursor-wait opacity-70" : "cursor-pointer",
                      ].join(" ")}
                      style={{ fontFamily: "inherit" }}
                    >
                      {isSaving ? "Saving..." : "Save Standard Costs"}
                    </button>
                    <button
                      type="button"
                      onClick={function () {
                        if (snapRef.current) setForm(snapRef.current);
                        setSavedMsg("");
                        setEditing(false);
                      }}
                      className="bg-white text-zephyr-text rounded-[7px] py-[9px] px-6 text-[14px] font-bold cursor-pointer"
                      style={{ fontFamily: "inherit", border: "1px solid #CFE0DE" }}
                    >
                      Cancel
                    </button>
                  </>
                )}
                {savedMsg && (
                  <span className="text-[13px] text-zephyr-teal font-semibold">{savedMsg}</span>
                )}
              </div>
              <div className="mt-[10px] text-[11px] text-zephyr-muted">
                Changes take effect immediately for new requests. Existing saved requests are not
                affected.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
